import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { buildFinancialApiUrl } from '../utils/financial-api-url.util';
import { normalizeApiDateTimeToBrazil } from '../utils/datetime.utils';
import {
  CostCenterBudgetProposal,
  CostCenterBudgetProposalCreate,
  CostCenterBudgetProposalFilters,
  CostCenterBudgetProposalPagedResult,
  CostCenterBudgetProposalReviewPayload
} from '../models/CostCenterBudgetProposal';
import { CostCenterBudgetHistoryEntry } from '../models/CostCenterBudgetHistory';
import {
  CostCenterExpenseBreakdown,
  CostCenterExpenseBreakdownCategory,
  CostCenterExpenseBreakdownItem,
  CostCenterExpenseBreakdownStatus
} from '../models/CostCenterExpenseBreakdown';
import { 
  CostCenter, 
  CostCenterCreate, 
  CostCenterUpdate, 
  CostCenterHierarchy,
  CircularReferenceValidation,
  ConsolidatedCost,
  ConsolidatedBudget,
  ConsolidatedStatistics,
  DescendantsResponse,
  HierarchyLevel
} from '../models/CostCenter';
import { ErrorService } from '../shared/services/error.service';

export interface CostCenterPagedResult {
  items: CostCenter[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface PaginationOptions {
  page?: number;
  pageSize?: number;
}

export interface CostCenterFilters extends PaginationOptions {
  status?: string;
  type?: string;
  department?: string;
  searchTerm?: string;
  search?: string; // Alias para searchTerm conforme documentação
}

/** Resposta do endpoint available-balance (saldo disponível do centro de custo). */
export interface AvailableBalanceResponse {
  costCenterId: number;
  costCenterName: string;
  costCenterCode: string;
  availableBalance: number;
  description?: string;
}

/** Resposta do endpoint budget-and-cost (orçamento vs. realizado). */
export interface BudgetAndCostResponse {
  costCenterId: number;
  costCenterName: string;
  costCenterCode: string;
  budget: number;
  cost: number;
  available: number;
  description?: string;
}

@Injectable({ providedIn: 'root' })
export class CostCentersService {

  private financialUrl(path = ''): string {
    return buildFinancialApiUrl('costcenters', path);
  }

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  // CRUD básico
  getAll(filters?: CostCenterFilters): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    
    // Parâmetros de paginação
    if (filters?.page) params = params.set('page', filters.page.toString());
    if (filters?.pageSize) params = params.set('pageSize', filters.pageSize.toString());
    
    // Parâmetros de filtro
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.type) params = params.set('type', filters.type);
    if (filters?.department) params = params.set('department', filters.department);
    
    // Busca por código ou nome (aceita searchTerm ou search conforme documentação)
    if (filters?.searchTerm) {
      params = params.set('searchTerm', filters.searchTerm);
    } else if (filters?.search) {
      params = params.set('searchTerm', filters.search);
    }
    
    return this.http.get<any>(this.financialUrl(), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { 
        this.errorService.showError('Erro ao buscar centros de custo');
        return throwError(() => err); 
      })
    );
  }

  getById(
    id: number,
    options?: { suppressErrorToast?: boolean }
  ): Observable<CostCenter> {
    return this.http.get<any>(this.financialUrl(`/${id}`)).pipe(
      map((dto) => this.mapFromDto(dto)),
      catchError((err) => {
        if (!options?.suppressErrorToast) {
          this.errorService.showError('Erro ao buscar centro de custo');
        }
        return throwError(() => err);
      })
    );
  }

  create(payload: CostCenterCreate): Observable<CostCenter> {
    const dto = this.mapToCreateDto(payload);
    return this.http.post<any>(this.financialUrl(), dto).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.showError('Erro ao criar centro de custo'); return throwError(() => err); })
    );
  }

  update(id: number, payload: CostCenterUpdate): Observable<CostCenter> {
    const dto = this.mapToUpdateDto(payload);
    return this.http.put<any>(this.financialUrl(`/${id}`), dto).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.showError('Erro ao atualizar centro de custo'); return throwError(() => err); })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(this.financialUrl(`/${id}`)).pipe(
      catchError(err => { this.errorService.showError('Erro ao excluir centro de custo'); return throwError(() => err); })
    );
  }

  // Consultas específicas
  /** GET `.../costcenters/code/{code}` — visualização por código (ex.: `33`). */
  getByCode(code: string, options?: { suppressErrorToast?: boolean }): Observable<CostCenter> {
    const c = encodeURIComponent((code || '').trim());
    return this.http.get<any>(this.financialUrl(`/code/${c}`)).pipe(
      map((dto) => this.mapFromDto(dto)),
      catchError((err) => {
        if (!options?.suppressErrorToast) {
          this.errorService.showError('Erro ao buscar centro de custo por código');
        }
        return throwError(() => err);
      })
    );
  }

  /** GET `.../costcenters/{id}/budget-history` — histórico de alterações de orçamento. */
  getBudgetHistory(
    costCenterId: number,
    options?: { suppressErrorToast?: boolean }
  ): Observable<CostCenterBudgetHistoryEntry[]> {
    return this.http.get<any>(this.financialUrl(`/${costCenterId}/budget-history`)).pipe(
      map((resp) => {
        const list = Array.isArray(resp) ? resp : Array.isArray(resp?.items) ? resp.items : [];
        return list.map((x: any) => this.mapBudgetHistoryFromDto(x));
      }),
      catchError((err) => {
        if (!options?.suppressErrorToast) {
          this.errorService.showError('Erro ao carregar histórico de orçamento do centro de custo');
        }
        return throwError(() => err);
      })
    );
  }

  /** GET `.../costcenters/{id}/expense-breakdown` — acompanhamento de gastos do centro. */
  getExpenseBreakdown(
    costCenterId: number,
    includeChildren = true,
    options?: { suppressErrorToast?: boolean }
  ): Observable<CostCenterExpenseBreakdown> {
    const params = new HttpParams().set('includeChildren', String(includeChildren));
    return this.http
      .get<any>(this.financialUrl(`/${costCenterId}/expense-breakdown`), { params })
      .pipe(
        map((dto) => this.mapExpenseBreakdownFromDto(dto)),
        catchError((err) => {
          if (!options?.suppressErrorToast) {
            this.errorService.showError('Erro ao carregar acompanhamento de gastos do centro de custo');
          }
          return throwError(() => err);
        })
      );
  }

  private mapExpenseBreakdownFromDto(dto: any): CostCenterExpenseBreakdown {
    const pick = (keys: string[]) => keys.map((k) => dto?.[k]).find((v) => v !== undefined && v !== null);
    const pickNum = (keys: string[]) => {
      const v = pick(keys);
      if (v === undefined || v === null || v === '') return 0;
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    const mapCategory = (x: any): CostCenterExpenseBreakdownCategory => ({
      category: String(x?.category ?? x?.Category ?? '—'),
      count: Number(x?.count ?? x?.Count ?? 0),
      totalPaid: Number(x?.totalPaid ?? x?.TotalPaid ?? 0),
      totalPending: Number(x?.totalPending ?? x?.TotalPending ?? 0)
    });
    const mapStatus = (x: any): CostCenterExpenseBreakdownStatus => ({
      status: String(x?.status ?? x?.Status ?? '—'),
      count: Number(x?.count ?? x?.Count ?? 0),
      totalAmount: Number(x?.totalAmount ?? x?.TotalAmount ?? 0)
    });
    const mapItem = (x: any): CostCenterExpenseBreakdownItem => {
      const p = (keys: string[]) => keys.map((k) => x?.[k]).find((v) => v !== undefined && v !== null);
      const pn = (keys: string[]) => {
        const v = p(keys);
        if (v === undefined || v === null || v === '') return undefined;
        const n = Number(v);
        return Number.isFinite(n) ? n : undefined;
      };
      return {
        accountsPayableId: Number(p(['accountsPayableId', 'AccountsPayableId']) ?? 0),
        costCenterId: Number(p(['costCenterId', 'CostCenterId']) ?? 0),
        costCenterName: p(['costCenterName', 'CostCenterName']),
        documentNumber: p(['documentNumber', 'DocumentNumber']),
        status: String(p(['status', 'Status']) ?? ''),
        category: p(['category', 'Category']),
        description: p(['description', 'Description']),
        expenseSummary: p(['expenseSummary', 'ExpenseSummary']),
        originalAmount: pn(['originalAmount', 'OriginalAmount']),
        discountAmount: pn(['discountAmount', 'DiscountAmount']),
        amountToPay: pn(['amountToPay', 'AmountToPay']),
        amountPaid: pn(['amountPaid', 'AmountPaid']),
        remainingAmount: pn(['remainingAmount', 'RemainingAmount']),
        expenseImpact: pn(['expenseImpact', 'ExpenseImpact']),
        issueDate: p(['issueDate', 'IssueDate']),
        dueDate: p(['dueDate', 'DueDate']),
        paymentDate: p(['paymentDate', 'PaymentDate']),
        paymentMethod: p(['paymentMethod', 'PaymentMethod']),
        invoiceNumber: p(['invoiceNumber', 'InvoiceNumber']),
        purchaseOrderNumber: p(['purchaseOrderNumber', 'PurchaseOrderNumber'])
      };
    };

    const byCategoryRaw = dto?.byCategory ?? dto?.ByCategory ?? [];
    const byStatusRaw = dto?.byStatus ?? dto?.ByStatus ?? [];
    const itemsRaw = dto?.items ?? dto?.Items ?? [];

    return {
      costCenterId: pickNum(['costCenterId', 'CostCenterId']),
      costCenterCode: String(pick(['costCenterCode', 'CostCenterCode']) ?? ''),
      costCenterName: String(pick(['costCenterName', 'CostCenterName']) ?? ''),
      includesChildCostCenters: Boolean(
        pick(['includesChildCostCenters', 'IncludesChildCostCenters']) ?? true
      ),
      consolidatedBudget: pickNum(['consolidatedBudget', 'ConsolidatedBudget']),
      totalPaid: pickNum(['totalPaid', 'TotalPaid']),
      totalPending: pickNum(['totalPending', 'TotalPending']),
      totalOverdue: pickNum(['totalOverdue', 'TotalOverdue']),
      availableBalance: pickNum(['availableBalance', 'AvailableBalance']),
      totalExpenseCount: pickNum(['totalExpenseCount', 'TotalExpenseCount']),
      byCategory: Array.isArray(byCategoryRaw) ? byCategoryRaw.map(mapCategory) : [],
      byStatus: Array.isArray(byStatusRaw) ? byStatusRaw.map(mapStatus) : [],
      items: Array.isArray(itemsRaw) ? itemsRaw.map(mapItem) : []
    };
  }

  private mapBudgetHistoryFromDto(dto: any): CostCenterBudgetHistoryEntry {
    const pick = (keys: string[]) => keys.map((k) => dto?.[k]).find((v) => v !== undefined && v !== null);
    return {
      id: Number(pick(['id', 'Id']) ?? 0),
      costCenterId: Number(pick(['costCenterId', 'CostCenterId']) ?? 0),
      costCenterBudgetProposalId: pick(['costCenterBudgetProposalId', 'CostCenterBudgetProposalId']),
      newMonthlyBudget: pick(['newMonthlyBudget', 'NewMonthlyBudget']),
      newAnnualBudget: pick(['newAnnualBudget', 'NewAnnualBudget']),
      effectiveAt: normalizeApiDateTimeToBrazil(pick(['effectiveAt', 'EffectiveAt'])),
      changedByStaffId: pick(['changedByStaffId', 'ChangedByStaffId']),
      changeType: String(pick(['changeType', 'ChangeType']) ?? ''),
      notes: pick(['notes', 'Notes']),
      createdAt: normalizeApiDateTimeToBrazil(pick(['createdAt', 'CreatedAt'])),
      updatedAt: normalizeApiDateTimeToBrazil(pick(['updatedAt', 'UpdatedAt'])),
      createdBy: pick(['createdBy', 'CreatedBy']),
      updatedBy: pick(['updatedBy', 'UpdatedBy'])
    };
  }

  getActive(options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl('/active'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar centros de custo ativos'); return throwError(() => err); })
    );
  }

  getInactive(options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl('/inactive'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar centros de custo inativos'); return throwError(() => err); })
    );
  }

  getByType(type: string, options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl(`/type/${encodeURIComponent(type)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar centros de custo por tipo'); return throwError(() => err); })
    );
  }

  getByCategory(category: string, options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl(`/category/${encodeURIComponent(category)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar centros de custo por categoria'); return throwError(() => err); })
    );
  }

  getByStatus(status: string, options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl(`/status/${encodeURIComponent(status)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar centros de custo por status'); return throwError(() => err); })
    );
  }

  getByLevel(level: number, options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl(`/level/${level}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar centros de custo por nível'); return throwError(() => err); })
    );
  }

  searchByName(name: string, options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl(`/search/${encodeURIComponent(name)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar centros de custo por nome'); return throwError(() => err); })
    );
  }

  getByDepartment(department: string, options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl(`/department/${encodeURIComponent(department)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar centros de custo por departamento'); return throwError(() => err); })
    );
  }

  getByBranch(branch: string, options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl(`/branch/${encodeURIComponent(branch)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar centros de custo por filial'); return throwError(() => err); })
    );
  }

  getByResponsible(responsible: string, options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl(`/responsible/${encodeURIComponent(responsible)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar centros de custo por responsável'); return throwError(() => err); })
    );
  }

  getByManager(managerStaffId: number, options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl(`/manager/${managerStaffId}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar centros de custo por gestor'); return throwError(() => err); })
    );
  }

  // Consultas hierárquicas
  getRootCostCenters(options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl('/root'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar centros de custo raiz'); return throwError(() => err); })
    );
  }

  getChildCostCenters(parentId: number, options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl(`/${parentId}/children`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar centros de custo filhos'); return throwError(() => err); })
    );
  }

  getHierarchy(id: number): Observable<CostCenter[]> {
    return this.http.get<any[]>(this.financialUrl(`/${id}/hierarchy`)).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.showError('Erro ao buscar hierarquia de centros de custo'); return throwError(() => err); })
    );
  }

  getDetails(options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl('/details'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar detalhes dos centros de custo'); return throwError(() => err); })
    );
  }

  // Validações
  checkCodeExists(code: string, excludeId?: number): Observable<{ exists: boolean }> {
    let params = new HttpParams();
    if (excludeId) {
      params = params.set('excludeId', excludeId.toString());
    }
    return this.http.get<{ exists: boolean }>(this.financialUrl(`/check-code/${encodeURIComponent(code)}`), { params }).pipe(
      catchError(err => { this.errorService.showError('Erro ao verificar código do centro de custo'); return throwError(() => err); })
    );
  }

  validate(costCenter: CostCenterCreate): Observable<{ isValid: boolean; errors: string[] }> {
    const dto = this.mapToCreateDto(costCenter);
    return this.http.post<{ isValid: boolean; errors: string[] }>(this.financialUrl('/validate'), dto).pipe(
      catchError(err => { this.errorService.showError('Erro ao validar centro de custo'); return throwError(() => err); })
    );
  }

  getTotalBudgetByType(type: string): Observable<{ totalBudget: number }> {
    return this.http.get<{ totalBudget: number }>(this.financialUrl(`/budget/total/${encodeURIComponent(type)}`)).pipe(
      catchError(err => { this.errorService.showError('Erro ao buscar orçamento total por tipo'); return throwError(() => err); })
    );
  }

  getTotalActualCostByType(type: string): Observable<{ totalActualCost: number }> {
    return this.http.get<{ totalActualCost: number }>(this.financialUrl(`/cost/total/${encodeURIComponent(type)}`)).pipe(
      catchError(err => { this.errorService.showError('Erro ao buscar custo total por tipo'); return throwError(() => err); })
    );
  }

  getByVariance(min?: number, max?: number, options?: PaginationOptions): Observable<CostCenterPagedResult> {
    let params = new HttpParams();
    if (min !== undefined && min !== null) params = params.set('min', String(min));
    if (max !== undefined && max !== null) params = params.set('max', String(max));
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(this.financialUrl('/variance'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { this.errorService.showError('Erro ao buscar centros de custo por variação'); return throwError(() => err); })
    );
  }

  // ========== NOVOS ENDPOINTS - Funcionalidades Avançadas ==========

  /**
   * 1. Validar Referência Circular
   * Valida se atribuir um novo pai criaria referência circular na hierarquia
   */
  validateParent(costCenterId: number, parentId: number): Observable<CircularReferenceValidation> {
    return this.http.get<CircularReferenceValidation>(
      this.financialUrl(`/${costCenterId}/validate-parent/${parentId}`)
    ).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao validar hierarquia do centro de custo');
        return throwError(() => err);
      })
    );
  }

  /**
   * 2. Custo Consolidado
   * Retorna o custo total consolidado (centro + todos descendentes)
   */
  getConsolidatedCost(costCenterId: number): Observable<ConsolidatedCost> {
    return this.http.get<ConsolidatedCost>(
      this.financialUrl(`/${costCenterId}/consolidated-cost`)
    ).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao buscar custo consolidado');
        return throwError(() => err);
      })
    );
  }

  /**
   * 3. Orçamento Consolidado
   * Retorna o orçamento total consolidado (centro + todos descendentes)
   */
  getConsolidatedBudget(costCenterId: number): Observable<ConsolidatedBudget> {
    return this.http.get<ConsolidatedBudget>(
      this.financialUrl(`/${costCenterId}/consolidated-budget`)
    ).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao buscar orçamento consolidado');
        return throwError(() => err);
      })
    );
  }

  /**
   * 4. Estatísticas Consolidadas
   * Retorna estatísticas (orçamento, custo, variação) para múltiplos centros
   */
  getConsolidatedStatistics(
    costCenterIds: number[],
    options?: { suppressErrorToast?: boolean }
  ): Observable<ConsolidatedStatistics> {
    return this.http.post<any>(this.financialUrl('/consolidated-statistics'), costCenterIds).pipe(
      map((dto) => this.mapConsolidatedStatisticsFromDto(dto)),
      catchError((err) => {
        if (!options?.suppressErrorToast) {
          this.errorService.showError('Erro ao buscar estatísticas consolidadas');
        }
        return throwError(() => err);
      })
    );
  }

  /** IDs raiz (sem pai na lista) para não duplicar totais pai + filho na consolidação. */
  resolveRootCostCenterIds(costCenters: CostCenter[]): number[] {
    const byId = new Map(costCenters.filter((c) => c.id).map((c) => [c.id, c]));
    const rootIds = new Set<number>();

    for (const cc of costCenters) {
      if (!cc.id) continue;
      let currentId: number | null = cc.id;
      const visited = new Set<number>();
      while (currentId) {
        if (visited.has(currentId)) break;
        visited.add(currentId);
        const item = byId.get(currentId);
        if (!item || item.parentId == null) {
          rootIds.add(currentId);
          break;
        }
        currentId = item.parentId ?? null;
      }
    }
    return Array.from(rootIds);
  }

  private mapConsolidatedStatisticsFromDto(dto: any): ConsolidatedStatistics {
    const pick = (o: any, keys: string[]) =>
      keys.map((k) => o?.[k]).find((v) => v !== undefined && v !== null);
    const pickNum = (o: any, keys: string[]) => {
      const v = pick(o, keys);
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    const summaryRaw = dto?.summary ?? dto?.Summary ?? {};
    const statsRaw = dto?.statistics ?? dto?.Statistics ?? [];

    return {
      totalCostCenters: pickNum(dto, ['totalCostCenters', 'TotalCostCenters']),
      warning: pick(dto, ['warning', 'Warning']),
      summary: {
        totalBudget: pickNum(summaryRaw, ['totalBudget', 'TotalBudget']),
        totalCost: pickNum(summaryRaw, ['totalCost', 'TotalCost']),
        overBudgetCount: pickNum(summaryRaw, ['overBudgetCount', 'OverBudgetCount'])
      },
      statistics: Array.isArray(statsRaw)
        ? statsRaw.map((s: any) => ({
            costCenterId: pickNum(s, ['costCenterId', 'CostCenterId']),
            budget: pickNum(s, ['budget', 'Budget']),
            cost: pickNum(s, ['cost', 'Cost']),
            variance: pickNum(s, ['variance', 'Variance']),
            status: (pick(s, ['status', 'Status']) ?? 'OK') as 'OK' | 'ATENÇÃO' | 'CRÍTICO'
          }))
        : []
    };
  }

  /**
   * 5. Todos os Descendentes
   * Retorna todos os descendentes (filhos, netos, bisnetos, etc.) na hierarquia
   */
  getAllDescendants(costCenterId: number): Observable<DescendantsResponse> {
    return this.http.get<DescendantsResponse>(
      this.financialUrl(`/${costCenterId}/descendants`)
    ).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao buscar descendentes do centro de custo');
        return throwError(() => err);
      })
    );
  }

  /**
   * 6. Nível Hierárquico
   * Retorna o nível hierárquico do centro de custo (0 = raiz, 1 = primeiro nível, etc.)
   */
  getHierarchyLevel(costCenterId: number): Observable<HierarchyLevel> {
    return this.http.get<HierarchyLevel>(
      this.financialUrl(`/${costCenterId}/hierarchy-level`)
    ).pipe(
      catchError(err => {
        this.errorService.showError('Erro ao buscar nível hierárquico');
        return throwError(() => err);
      })
    );
  }

  /**
   * Saldo disponível do centro de custo (orçamento − custo realizado).
   * Uso: validar ou exibir saldo antes/depois de pagar (quando a conta tem costCenterId).
   * Aceita `suppressErrorToast` quando a tela faz tratamento próprio do erro.
   */
  getAvailableBalance(
    costCenterId: number,
    options?: { suppressErrorToast?: boolean }
  ): Observable<AvailableBalanceResponse> {
    return this.http.get<AvailableBalanceResponse>(
      this.financialUrl(`/${costCenterId}/available-balance`)
    ).pipe(
      map(dto => this.mapAvailableBalanceFromDto(dto)),
      catchError(err => {
        if (!options?.suppressErrorToast) {
          this.errorService.showError('Erro ao buscar saldo do centro de custo');
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Orçamento, custo realizado e saldo (relatório Orçamento vs. Realizado).
   */
  getBudgetAndCost(costCenterId: number): Observable<BudgetAndCostResponse> {
    return this.http.get<BudgetAndCostResponse>(
      this.financialUrl(`/${costCenterId}/budget-and-cost`)
    ).pipe(
      map(dto => this.mapBudgetAndCostFromDto(dto)),
      catchError(err => {
        this.errorService.showError('Erro ao buscar orçamento e custo do centro de custo');
        return throwError(() => err);
      })
    );
  }

  private mapAvailableBalanceFromDto(dto: any): AvailableBalanceResponse {
    const pick = (o: any, keys: string[]) => keys.map(k => o?.[k]).find(v => v !== undefined);
    return {
      costCenterId: pick(dto, ['costCenterId', 'CostCenterId']) ?? 0,
      costCenterName: pick(dto, ['costCenterName', 'CostCenterName']) ?? '',
      costCenterCode: pick(dto, ['costCenterCode', 'CostCenterCode']) ?? '',
      availableBalance: Number(pick(dto, ['availableBalance', 'AvailableBalance']) ?? 0),
      description: pick(dto, ['description', 'Description'])
    };
  }

  private mapBudgetAndCostFromDto(dto: any): BudgetAndCostResponse {
    const pick = (o: any, keys: string[]) => keys.map(k => o?.[k]).find(v => v !== undefined);
    return {
      costCenterId: pick(dto, ['costCenterId', 'CostCenterId']) ?? 0,
      costCenterName: pick(dto, ['costCenterName', 'CostCenterName']) ?? '',
      costCenterCode: pick(dto, ['costCenterCode', 'CostCenterCode']) ?? '',
      budget: Number(pick(dto, ['budget', 'Budget']) ?? 0),
      cost: Number(pick(dto, ['cost', 'Cost']) ?? 0),
      available: Number(pick(dto, ['available', 'Available']) ?? 0),
      description: pick(dto, ['description', 'Description'])
    };
  }

  // Mapeamentos DTO <-> Front
  private mapFromDto(dto: any): CostCenter {
    const pick = (o: any, keys: string[]) =>
      keys.map((k) => o?.[k]).find((v) => v !== undefined && v !== null);
    const pickNum = (keys: string[]) => {
      const v = pick(dto, keys);
      if (v === undefined || v === null || v === '') return undefined;
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };
    const toIso = (d: any) => (d ? new Date(d).toISOString() : undefined);

    const monthlyBudget = pickNum([
      'monthlyBudget',
      'MonthlyBudget',
      'budget',
      'Budget'
    ]);
    const annualBudget = pickNum(['annualBudget', 'AnnualBudget']);
    const childList =
      dto?.childCostCenters ?? dto?.ChildCostCenters ?? dto?.children ?? dto?.Children;
    const historyList =
      dto?.budgetHistories ?? dto?.BudgetHistories ?? [];

    return {
      id: pick(dto, ['Id', 'id']) ?? 0,
      code: pick(dto, ['Code', 'code']) ?? '',
      name: pick(dto, ['Name', 'name']) ?? '',
      description: pick(dto, ['Description', 'description']),
      type: pick(dto, ['Type', 'type']) ?? '',
      status: pick(dto, ['Status', 'status']) ?? 'Ativo',
      parentId: pick(dto, ['ParentCostCenterId', 'ParentId', 'parentId']),
      isActive: pick(dto, ['IsActive', 'isActive']) ?? true,
      monthlyBudget,
      annualBudget,
      budget: monthlyBudget,
      actualCost: pickNum(['actualCost', 'ActualCost']),
      responsible: pick(dto, [
        'responsiblePerson',
        'ResponsiblePerson',
        'Responsible',
        'responsible'
      ]),
      managerStaffId: pickNum(['managerStaffId', 'ManagerStaffId']),
      managerStaffName: pick(dto, ['managerStaffName', 'ManagerStaffName']),
      category: pick(dto, ['category', 'Category']),
      branch: pick(dto, ['branch', 'Branch']),
      department: pick(dto, ['Department', 'department']),
      acceptsCosts: pick(dto, ['acceptsCosts', 'AcceptsCosts']),
      acceptsRevenues: pick(dto, ['acceptsRevenues', 'AcceptsRevenues']),
      currencyCode: pick(dto, ['currencyCode', 'CurrencyCode']) ?? 'BRL',
      startDate: toIso(pick(dto, ['StartDate', 'startDate'])),
      endDate: toIso(pick(dto, ['EndDate', 'endDate'])),
      notes: pick(dto, ['Notes', 'notes']),
      level: pick(dto, ['Level', 'level']),
      path: pick(dto, ['HierarchyPath', 'Path', 'path']),
      createdAt: toIso(pick(dto, ['CreatedAt', 'createdAt'])),
      updatedAt: toIso(pick(dto, ['UpdatedAt', 'updatedAt'])),
      createdBy: pick(dto, ['CreatedBy', 'createdBy']),
      updatedBy: pick(dto, ['UpdatedBy', 'updatedBy']),
      parentName: pick(dto, ['ParentCostCenterName', 'ParentName', 'parentName']),
      hasChildren:
        pick(dto, ['HasChildren', 'hasChildren']) ??
        (Array.isArray(childList) && childList.length > 0),
      children: Array.isArray(childList) ? this.mapListFromDto(childList) : undefined,
      budgetHistories: Array.isArray(historyList)
        ? historyList.map((x: any) => this.mapBudgetHistoryFromDto(x))
        : undefined
    };
  }

  private mapListFromDto(list: any[]): CostCenter[] {
    if (!list || !Array.isArray(list)) return [];
    return list.map(x => this.mapFromDto(x));
  }

  /** PUT `.../costcenters/{id}` — corpo em camelCase conforme contrato da API. */
  private mapToUpdateDto(payload: Partial<CostCenterUpdate> & Record<string, unknown>): Record<string, unknown> {
    const dto: Record<string, unknown> = {
      code: payload.code,
      name: payload.name,
      description: payload.description ?? '',
      type: payload.type,
      department: payload.department ?? '',
      status: payload.status,
      isActive: payload.isActive ?? true,
      acceptsCosts: payload.acceptsCosts ?? true,
      acceptsRevenues: payload.acceptsRevenues ?? false
    };

    const category = payload['category'];
    if (category != null && String(category).trim() !== '') {
      dto['category'] = String(category).trim();
    }

    const branch = payload['branch'];
    if (branch != null && String(branch).trim() !== '') {
      dto['branch'] = String(branch).trim();
    }

    const managerStaffId = payload['managerStaffId'];
    if (managerStaffId != null && !Number.isNaN(Number(managerStaffId))) {
      dto['managerStaffId'] = Number(managerStaffId);
    }

    const parentRaw = payload['parentCostCenterId'] ?? payload['parentId'];
    if (parentRaw != null && parentRaw !== '' && !Number.isNaN(Number(parentRaw))) {
      dto['parentCostCenterId'] = Number(parentRaw);
    } else {
      dto['parentCostCenterId'] = null;
    }

    dto['notes'] = payload['notes'] ?? '';

    const startDate = this.formatCostCenterDate(payload['startDate']);
    if (startDate) {
      dto['startDate'] = startDate;
    }

    const endDate = this.formatCostCenterDate(payload['endDate']);
    dto['endDate'] = endDate;

    return dto;
  }

  /** Data do formulário (`YYYY-MM-DD` ou ISO) para o contrato da API. */
  private formatCostCenterDate(value: unknown): string | null {
    if (value == null || value === '') {
      return null;
    }
    const s = String(value).trim();
    if (!s) {
      return null;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return `${s}T00:00:00.000Z`;
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }

  /** POST criação — camelCase + orçamento mensal/anual. */
  private mapToCreateDto(payload: Partial<CostCenterCreate> & Record<string, unknown>): Record<string, unknown> {
    const monthly =
      payload.monthlyBudget ?? payload.budget ?? undefined;
    const annual =
      payload.annualBudget ??
      (monthly != null ? Number(monthly) * 12 : undefined);

    const base = this.mapToUpdateDto(payload);
    return {
      ...base,
      monthlyBudget: monthly,
      annualBudget: annual
    };
  }

  // ===== Propostas de orçamento (indicação de verba por centro de custo) =====

  getBudgetProposals(
    filters?: CostCenterBudgetProposalFilters
  ): Observable<CostCenterBudgetProposalPagedResult> {
    let params = new HttpParams();
    if (filters?.page) {
      params = params.set('page', String(filters.page));
    }
    if (filters?.pageSize) {
      params = params.set('pageSize', String(filters.pageSize));
    }
    if (filters?.status?.trim()) {
      params = params.set('status', filters.status.trim());
    }
    if (filters?.costCenterId != null && filters.costCenterId > 0) {
      params = params.set('costCenterId', String(filters.costCenterId));
    }

    return this.http.get<any>(this.financialUrl('/budget-proposals'), { params }).pipe(
      map((resp) => this.mapBudgetProposalPagedResponse(resp)),
      catchError((err) => {
        this.errorService.showError('Erro ao buscar propostas de orçamento');
        return throwError(() => err);
      })
    );
  }

  /** POST `.../costcenters/{costCenterId}/budget-proposals` */
  createBudgetProposal(payload: CostCenterBudgetProposalCreate): Observable<CostCenterBudgetProposal> {
    const costCenterId = Number(payload.costCenterId);
    const body = {
      proposedMonthlyBudget: payload.proposedMonthlyBudget,
      proposedAnnualBudget: payload.proposedAnnualBudget,
      justification: payload.justification,
      requestedByStaffId: payload.requestedByStaffId,
      requestedAt: payload.requestedAt
    };
    return this.http.post<any>(this.financialUrl(`/${costCenterId}/budget-proposals`), body).pipe(
      map((dto) => this.mapBudgetProposalFromDto(dto)),
      catchError((err) => {
        this.errorService.showError('Erro ao registrar proposta de orçamento');
        return throwError(() => err);
      })
    );
  }

  /** GET `.../costcenters/budget-proposals/{id}` — detalhe da proposta. */
  getBudgetProposalById(
    id: number,
    options?: { suppressErrorToast?: boolean }
  ): Observable<CostCenterBudgetProposal> {
    return this.http.get<any>(this.financialUrl(`/budget-proposals/${id}`)).pipe(
      map((dto) => this.mapBudgetProposalFromDto(dto)),
      catchError((err) => {
        if (!options?.suppressErrorToast) {
          this.errorService.showError('Erro ao carregar proposta de orçamento');
        }
        return throwError(() => err);
      })
    );
  }

  /** POST `.../costcenters/budget-proposals/{id}/approve` */
  approveBudgetProposal(
    id: number,
    payload: CostCenterBudgetProposalReviewPayload
  ): Observable<CostCenterBudgetProposal> {
    return this.http
      .post<any>(this.financialUrl(`/budget-proposals/${id}/approve`), payload)
      .pipe(
        map((dto) => this.mapBudgetProposalFromDto(dto)),
        catchError((err) => {
          this.errorService.showError('Erro ao aprovar proposta de orçamento');
          return throwError(() => err);
        })
      );
  }

  /** POST `.../costcenters/budget-proposals/{id}/reject` */
  rejectBudgetProposal(
    id: number,
    payload: CostCenterBudgetProposalReviewPayload
  ): Observable<CostCenterBudgetProposal> {
    return this.http
      .post<any>(this.financialUrl(`/budget-proposals/${id}/reject`), payload)
      .pipe(
        map((dto) => this.mapBudgetProposalFromDto(dto)),
        catchError((err) => {
          this.errorService.showError('Erro ao recusar proposta de orçamento');
          return throwError(() => err);
        })
      );
  }

  private mapBudgetProposalFromDto(dto: any): CostCenterBudgetProposal {
    const pick = (keys: string[]) => keys.map((k) => dto?.[k]).find((v) => v !== undefined && v !== null);
    const code = pick(['costCenterCode', 'CostCenterCode']);
    const name = pick(['costCenterName', 'CostCenterName']);
    return {
      id: Number(pick(['id', 'Id']) ?? 0),
      costCenterId: Number(pick(['costCenterId', 'CostCenterId']) ?? 0),
      costCenterCode: code != null ? String(code).trim() : undefined,
      costCenterName: name != null ? String(name).trim() : undefined,
      proposedMonthlyBudget: pick(['proposedMonthlyBudget', 'ProposedMonthlyBudget']),
      proposedAnnualBudget: pick(['proposedAnnualBudget', 'ProposedAnnualBudget']),
      justification: pick(['justification', 'Justification']),
      requestedByStaffId: pick(['requestedByStaffId', 'RequestedByStaffId']),
      requestedAt: normalizeApiDateTimeToBrazil(pick(['requestedAt', 'RequestedAt'])),
      status: String(pick(['status', 'Status']) ?? 'Pending'),
      createdAt: pick(['createdAt', 'CreatedAt']),
      updatedAt: pick(['updatedAt', 'UpdatedAt']),
      createdBy: pick(['createdBy', 'CreatedBy']),
      updatedBy: pick(['updatedBy', 'UpdatedBy']),
      reviewedByStaffId: pick(['reviewedByStaffId', 'ReviewedByStaffId']),
      reviewComments: pick(['reviewComments', 'ReviewComments']),
      reviewedAt: normalizeApiDateTimeToBrazil(pick(['reviewedAt', 'ReviewedAt']))
    };
  }

  private mapBudgetProposalPagedResponse(response: any): CostCenterBudgetProposalPagedResult {
    const rawItems = Array.isArray(response?.items)
      ? response.items
      : Array.isArray(response?.Items)
        ? response.Items
        : [];
    const items = rawItems.map((x: any) => this.mapBudgetProposalFromDto(x));
    const page = response?.page ?? response?.Page ?? 1;
    const pageSize = response?.pageSize ?? response?.PageSize ?? items.length;
    const totalItems =
      response?.totalItems ?? response?.TotalItems ?? response?.totalRecords ?? items.length;
    const totalPages =
      response?.totalPages ?? response?.TotalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);
    return { items, page, pageSize, totalItems, totalPages };
  }

  private mapPagedResponse(response: any): CostCenterPagedResult {
    const rawItems = Array.isArray(response?.Items)
      ? response.Items
      : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

    const items = rawItems.map((item: any) => this.mapFromDto(item));

    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? items.length;
    const totalItems = response?.TotalItems ?? response?.totalItems ?? response?.TotalRecords ?? response?.totalRecords ?? items.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return {
      items,
      page,
      pageSize,
      totalItems,
      totalPages,
    };
  }
}
